import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Modal, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import 'Components/Product/ProductOffers/ValidCoupon.scss';
import 'Components/Account/AccountModal.scss';

import { currencyCode } from 'Helpers/Geo/data';
import { addVoucher } from 'Redux/ducks/voucher';
import { toggleFeedbackPopup } from 'Redux/ducks/feedback-popups';
import Terms from 'Components/Vouchers/Terms';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';
import useViewport from 'Hooks/useViewport';

function ValidCoupon({
  intl,
  voucher,
  reduxVoucher,
  addVoucher: propsAddVoucher,
  toggleFeedbackPopup: propsToggleFeedbackPopup,
  siteBrand,
}) {
  const messages = defineMessages({
    error_title: {
      id: 'valid-coupon-banner.popup_error_title',
      defaultMessage: 'Sorry, your coupon could not be applied',
    },
    error_message: {
      id: 'valid-coupon-banner.popup_error_message',
      defaultMessage: 'The coupon code may be invalid, expired or not yet active',
    },
    success_title: {
      id: 'valid-coupon-banner.popup_success_title',
      defaultMessage: 'Your coupon has applied successfully!',
    },
  });

  const [openModal, setOpenModal] = useState(false);

  const { isAboveSm } = useViewport();

  const { formatMessage } = intl;

  let voucherCode = voucher.code;
  if (siteBrand === 'hpomen' && voucher.code === 'FANATICAL15') voucherCode = 'OMEN15';

  const applyVoucher = async (code) => {
    const appliedVoucher = await propsAddVoucher(code);
    if (isEmpty(appliedVoucher)) {
      propsToggleFeedbackPopup({ type: 'error', title: formatMessage(messages.error_title), message: formatMessage(messages.error_message) });
    }

    if (appliedVoucher.code === voucherCode) {
      propsToggleFeedbackPopup({ type: 'success', title: formatMessage(messages.success_title), message: voucher.title || voucher.name });
    }
  };

  let voucherApplied = false;
  const reduxVoucherCode = get(reduxVoucher, 'voucher.code', false);
  if (reduxVoucherCode === voucherCode) voucherApplied = true;

  let minSpend = false;
  if (voucher && voucher.min_spend && voucher.min_spend[`${currencyCode}`] > 0) {
    minSpend = true;
  }

  return (
    <React.Fragment>
      <div className="valid-coupon-delivery-banner">
        <div className="circle-start" />
        <div className="percent-container">
          <div className="percent-number">
            {voucher.percent}
          </div>
          <div className="percent-symbol-container">
            <div className="percent-symbol">
              %
            </div>
            <div className="percent-off">
              off
            </div>
          </div>
        </div>
        <div className="description-container">
          <div className="description-text-container">
            <div className="description-subheading">
              <FormattedMessage
                id="ValidCoupon.save"
                defaultMessage="Save"
              />
              &nbsp;
              {voucher.percent}%
              &nbsp;
              <FormattedMessage
                values={{
                  code: <span className="coupon-code">{voucherCode}</span>,
                }}
                id="ValidCoupon.code"
                defaultMessage="with code {code}"
              />
              {minSpend && <>
                &nbsp;
                <FormattedMessage
                  id="valid-coupon.terms.min-spend"
                  defaultMessage="when you spend atleast {min}"
                  values={{
                    min: <SpecialFormattedNumber value={voucher.min_spend[`${currencyCode}`] / 100} style="currency" />,
                  }}
                />
              </>}
              &nbsp;
              <button
                className="description-exclusion-text"
                onClick={() => setOpenModal(!openModal)}
              >
                <FormattedMessage
                  id="ValidCoupon.exclusions-apply"
                  defaultMessage="Exclusions Apply"
                />
              </button>
            </div>
          </div>
          {!voucherApplied ?
            <button
              className="description-button-apply"
              onClick={() => { applyVoucher(voucherCode); }}
            >
              <div className="description-button-text">
                <FormattedMessage
                  id="ValidCoupon.button-text-apply"
                  defaultMessage="Apply Coupon"
                />
              </div>
            </button>
            :
            <div className="description-button-applied">
              <div className="description-button-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" role="img">
                  <g clipPath="url(#clip0_579_339815)">
                    <path d="M14.6667 7.38674V8.00007C14.6659 9.43769 14.2004 10.8365 13.3396 11.988C12.4788 13.1394 11.2689 13.9817 9.89028 14.3893C8.51166 14.797 7.03821 14.748 5.68969 14.2498C4.34116 13.7516 3.18981 12.8308 2.40735 11.6248C1.62488 10.4188 1.25323 8.99212 1.34783 7.55762C1.44242 6.12312 1.99818 4.75762 2.93223 3.66479C3.86628 2.57195 5.12856 1.81033 6.53083 1.4935C7.9331 1.17668 9.40022 1.32163 10.7134 1.90674" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6 7.33317L8 9.33317L14.6667 2.6665" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_579_339815">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              {isAboveSm &&
                <FormattedMessage
                  id="valid-coupon.message.coupon-applied"
                  defaultMessage="Coupon Applied"
                />
              }
              {!isAboveSm &&
                <FormattedMessage
                  id="valid-coupon.message.applied"
                  defaultMessage="Applied"
                />
              }
            </div>
          }
        </div>
        <div className="circle-end" />
      </div>

      <Modal isOpen={openModal} toggle={() => setOpenModal(!openModal)} className="account-modal">
        <ModalBody>
          <div className="account-modal-title">
            <FormattedMessage
              id="valid-coupon.modal.terms.title"
              defaultMessage="Coupon Terms and Conditions"
            />
          </div>
          <div className="account-modal-form">
            <p>
              {voucher.description}
            </p>
            <div>
              Code: {voucherCode}
            </div>
            <Terms voucher={voucher} />
          </div>
        </ModalBody>

        <div className="close-button-container">
          <button aria-label="close" onClick={() => setOpenModal(!openModal)}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
}

ValidCoupon.propTypes = {
  intl: PropTypes.shape({}),
  voucher: PropTypes.shape({}),
  reduxVoucher: PropTypes.shape({}),
  addVoucher: PropTypes.func,
  toggleFeedbackPopup: PropTypes.func,
  siteBrand: PropTypes.string,
};

ValidCoupon.defaultProps = {
  intl: {},
  voucher: {},
  reduxVoucher: {},
  addVoucher: () => { },
  toggleFeedbackPopup: () => { },
  siteBrand: 'fanatical',
};

const mapStateToProps = ({ voucher, siteBrand }) => ({ reduxVoucher: voucher, siteBrand });
const mapDispatchToProps = { addVoucher, toggleFeedbackPopup };

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ValidCoupon));
